import React, { useState } from "react";
import { toast } from "sonner";
import { Copy, Check } from "@phosphor-icons/react";

/**
 * One-click copy-paste block for Amazon listing fields.
 * - title: small caps mono label above the box
 * - value: string OR string[] (rendered as ordered list for bullets)
 * - hint: small helper text under the box (e.g. "≤ 250 bytes")
 * - tone: 'default' | 'accent' (uses persimmon highlight)
 */
export default function CopyBlock({
  title,
  value,
  hint,
  testid,
  tone = "default",
  charLimit,
}) {
  const [copied, setCopied] = useState(false);
  if (value == null || (Array.isArray(value) && value.length === 0)) return null;

  const text = Array.isArray(value) ? value.join("\n") : String(value);
  const byteLen = new Blob([text]).size;
  const limit = charLimit;
  const over = limit && byteLen > limit;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success(`Copied · ${title}`);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Couldn't copy — long-press to copy manually");
    }
  };

  const accent = tone === "accent";

  return (
    <div className="border border-rule bg-white" data-testid={testid}>
      <div className={`flex items-center justify-between border-b border-rule px-4 py-2 ${accent ? "bg-persimmon-soft" : ""}`}>
        <span className="label-mono">{title}</span>
        <button
          type="button"
          onClick={copy}
          className={`inline-flex items-center gap-1.5 label-mono px-2 py-1 border transition-colors ${
            copied ? "border-signal text-signal bg-signal-bg" : "border-ink text-ink hover:bg-ink hover:text-white"
          }`}
          data-testid={testid ? `${testid}-copy` : undefined}
        >
          {copied ? (<><Check weight="bold" className="w-3 h-3" /> Copied</>)
                  : (<><Copy weight="bold" className="w-3 h-3" /> Copy</>)}
        </button>
      </div>
      <div className="p-4">
        {Array.isArray(value) ? (
          <ol className="space-y-2">
            {value.map((line, i) => (
              <li key={`${title}-${i}`} className="flex items-start gap-3 text-sm leading-relaxed">
                <span className="font-mono tabular text-ink/40 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                <span className="text-ink whitespace-pre-line">{line}</span>
              </li>
            ))}
          </ol>
        ) : (
          <div className="text-sm text-ink leading-relaxed whitespace-pre-line">{text}</div>
        )}
      </div>
      {(hint || limit) && (
        <div className={`border-t border-rule px-4 py-2 flex items-center justify-between label-mono ${over ? "text-alert" : ""}`}>
          <span>{hint}</span>
          {limit != null && (
            <span className={over ? "text-alert font-bold" : "text-ink/50"}>
              {byteLen} / {limit} bytes {over && "· OVER"}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
